import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import type {
  AllocateResponsibleUserRequest,
  EndInvolvementsRequest,
  FoundUser,
  OfferedEndingCause,
  Organisation,
  OrganisationId,
  OrganisationType,
  PersonInvolvement,
  PersonInvolvementId,
  PersonInvolvementKind,
  PersonState,
  ReplaceResponsibleUserRequest,
  UserId,
} from "@thalamos/haven-common";
import {
  AllocateResponsibleUserRequestSchema,
  EndInvolvementsRequestSchema,
  endOfDay,
  isInvolvementEndingOn,
  isReasonNeededForEndingCause,
  offeredEndingCausesByPartyKind,
  OfferedEndingCauseSchema,
  ReplaceResponsibleUserRequestSchema,
  today,
} from "@thalamos/haven-common";

import { CancelOrProceedActions } from "../CancelOrProceedActions/CancelOrProceedActions.js";
import { UserPicker, type ChosenUser } from "../PartyPickers/PartyPickers.js";

/**
 * Whether a change to who is involved succeeded. `changed` when it was refused
 * because somebody else changed who is involved since the page was read, which
 * the person in front of the dialog can act on by reading it again; one
 * `failed` for every other way it did not succeed.
 */
export type InvolvementOutcome = "done" | "changed" | "failed";

/** Where a dialog's change stands, once it has been sent. */
export type InvolvementChangeStatus =
  "submitting" | Exclude<InvolvementOutcome, "done">;

/**
 * Says a change was refused because what it was made against has changed,
 * offering to read it again and closing the dialog once it has.
 */
export const ChangedSinceOpenedAlert = ({
  message,
  onRefresh,
  onClose,
}: {
  message: string;
  onRefresh: () => Promisable<void>;
  onClose: () => Promisable<void>;
}) => {
  const { t } = useTranslation();
  const [refreshing, setRefreshing] = useState(false);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
    await onClose();
  };

  return (
    <Alert
      severity="error"
      action={
        <Button
          color="inherit"
          size="small"
          disabled={refreshing}
          onClick={() => {
            void refresh();
          }}
        >
          {t("access.refresh")}
        </Button>
      }
    >
      {message}
    </Alert>
  );
};

/** The outcome of a change, with a throw counted as one that did not land. */
export const outcomeOfChange = async (
  change: () => Promisable<InvolvementOutcome>,
): Promise<InvolvementOutcome> => {
  try {
    return await change();
  } catch {
    return "failed";
  }
};

/**
 * Everything a dialog needs to find a party, and to say whether one is part of
 * this child's care.
 *
 * Injected rather than fetched here, so a story shows any of these dialogs
 * against parties it chose and no dialog knows about the API.
 */
export type PartySearch = {
  findUsers: (term: string) => Promise<FoundUser[]>;
  findOrganisations: (
    types: readonly OrganisationType[],
    term: string,
  ) => Promise<Organisation[]>;
  /** The bodies with a live involvement with this child, plus the acting one. */
  near: readonly OrganisationId[];
};

export type AllocateResponsibleUserDialogProps = {
  open: boolean;
  onClose: () => Promisable<void>;
  search: PartySearch;
  onAllocateResponsibleUser: (
    request: AllocateResponsibleUserRequest,
  ) => Promisable<InvolvementOutcome>;
  onRefresh: () => Promisable<void>;
};

/**
 * Names who runs a case nobody runs. Only the person and the body they run it
 * for: a responsible user holds the case until they hand it on, so there is no part to
 * choose and no end date to give.
 */
export const AllocateResponsibleUserDialog = ({
  open,
  onClose,
  search,
  onAllocateResponsibleUser,
  onRefresh,
}: AllocateResponsibleUserDialogProps) => {
  const { t } = useTranslation();
  const [chosen, setChosen] = useState<ChosenUser | null>(null);
  const [status, setStatus] = useState<InvolvementChangeStatus | undefined>(
    undefined,
  );

  const close = () => {
    setChosen(null);
    setStatus(undefined);
    void onClose();
  };

  // Running a case is standing at a body, so somebody with none to run it for
  // cannot be named.
  const membership = chosen?.membership ?? null;

  const submit = async () => {
    if (chosen === null || membership === null) {
      return;
    }

    const request = AllocateResponsibleUserRequestSchema.safeParse({
      userId: chosen.user.id,
      organisationId: membership.organisationId,
    });

    // Unreachable: both ids come from a parsed user and a parsed membership, and
    // submit waits for a membership. Kept so a drift fails politely.
    /* v8 ignore if */
    if (!request.success) {
      setStatus("failed");
      return;
    }

    setStatus("submitting");
    const outcome = await outcomeOfChange(() =>
      onAllocateResponsibleUser(request.data),
    );

    if (outcome === "done") {
      close();
      return;
    }

    setStatus(outcome);
  };

  return (
    <Dialog
      open={open}
      onClose={status === "submitting" ? undefined : close}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>{t("access.allocatingResponsibleUser.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <DialogContentText>
            {t("access.allocatingResponsibleUser.description")}
          </DialogContentText>

          <UserPicker
            kind="responsible_user"
            value={chosen}
            onChange={setChosen}
            onFind={search.findUsers}
            near={search.near}
          />

          {match(status)
            .with(undefined, "submitting", () => null)
            .with("failed", () => (
              <Alert severity="error">
                {t("access.allocatingResponsibleUser.failed")}
              </Alert>
            ))
            .with("changed", () => (
              <ChangedSinceOpenedAlert
                message={t("access.allocatingResponsibleUser.changed")}
                onRefresh={onRefresh}
                onClose={close}
              />
            ))
            .exhaustive()}
        </Stack>
      </DialogContent>
      <CancelOrProceedActions
        cancel={{
          onClick: close,
          disabled: status === "submitting",
        }}
        proceed={{
          label: t("access.allocatingResponsibleUser.submit"),
          onClick: submit,
          disabled:
            membership === null ||
            status === "submitting" ||
            status === "changed",
          kind: "primary",
        }}
      />
    </Dialog>
  );
};

export type ReplaceResponsibleUserDialogProps = {
  open: boolean;
  onClose: () => Promisable<void>;
  search: PartySearch;
  /** Whoever runs the case now, who ends as the successor starts. */
  outgoing: {
    involvementId: PersonInvolvementId;
    userId: UserId;
    organisationId: OrganisationId | null;
    name: string;
  };
  onReplaceResponsibleUser: (
    request: ReplaceResponsibleUserRequest,
  ) => Promisable<InvolvementOutcome>;
  onRefresh: () => Promisable<void>;
};

/**
 * Hands a case somebody runs to a successor, with a reason, warning what the
 * outgoing holder loses before anything changes.
 */
export const ReplaceResponsibleUserDialog = ({
  open,
  onClose,
  search,
  outgoing,
  onReplaceResponsibleUser,
  onRefresh,
}: ReplaceResponsibleUserDialogProps) => {
  const { t } = useTranslation();
  const [chosen, setChosen] = useState<ChosenUser | null>(null);
  const [reason, setReason] = useState("");
  const [status, setStatus] = useState<InvolvementChangeStatus | undefined>(
    undefined,
  );

  const close = () => {
    setChosen(null);
    setReason("");
    setStatus(undefined);
    void onClose();
  };

  const membership = chosen?.membership ?? null;
  const alreadyRunsIt =
    chosen?.user.id === outgoing.userId &&
    membership?.organisationId === outgoing.organisationId;
  const ready =
    membership !== null && !alreadyRunsIt && reason.trim().length > 0;

  const submit = async () => {
    if (chosen === null || membership === null) {
      return;
    }

    const request = ReplaceResponsibleUserRequestSchema.safeParse({
      involvementId: outgoing.involvementId,
      userId: chosen.user.id,
      organisationId: membership.organisationId,
      reason,
    });

    // Unreachable while the dialog's own checks match the schema: submit needs
    // a non-blank reason, the box stops at the schema's length, and every id
    // comes from parsed data. Kept so a drift between them fails politely.
    /* v8 ignore if */
    if (!request.success) {
      setStatus("failed");
      return;
    }

    setStatus("submitting");
    const outcome = await outcomeOfChange(() =>
      onReplaceResponsibleUser(request.data),
    );

    if (outcome === "done") {
      close();
      return;
    }

    setStatus(outcome);
  };

  return (
    <Dialog
      open={open}
      onClose={status === "submitting" ? undefined : close}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>{t("access.replacingResponsibleUser.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="warning">
            {t("access.replacingResponsibleUser.warning", {
              name: outgoing.name,
            })}
          </Alert>

          <UserPicker
            kind="responsible_user"
            value={chosen}
            onChange={setChosen}
            onFind={search.findUsers}
            near={search.near}
          />

          {alreadyRunsIt ? (
            <Alert severity="info">
              {t("access.replacingResponsibleUser.alreadyRunsIt", {
                name: outgoing.name,
              })}
            </Alert>
          ) : null}

          <TextField
            label={t("access.replacingResponsibleUser.reason")}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
            multiline
            minRows={2}
            required
            slotProps={{ htmlInput: { maxLength: 4000 } }}
          />

          {match(status)
            .with(undefined, "submitting", () => null)
            .with("failed", () => (
              <Alert severity="error">
                {t("access.replacingResponsibleUser.failed")}
              </Alert>
            ))
            .with("changed", () => (
              <ChangedSinceOpenedAlert
                message={t("access.replacingResponsibleUser.changed")}
                onRefresh={onRefresh}
                onClose={close}
              />
            ))
            .exhaustive()}
        </Stack>
      </DialogContent>
      <CancelOrProceedActions
        cancel={{
          onClick: close,
          disabled: status === "submitting",
        }}
        proceed={{
          label: t("access.replacingResponsibleUser.submit"),
          onClick: submit,
          disabled: !ready || status === "submitting" || status === "changed",
          kind: "primary",
        }}
      />
    </Dialog>
  );
};

/**
 * The roles whose ending warns that an active person will become unassigned.
 * A product choice, not derived from `requiredInvolvementKinds`, and it need not
 * match it.
 */
const WARNS_UNASSIGNED: readonly PersonInvolvementKind[] = [
  "duty_of_care",
  "designated_assessor",
  "approver",
];

const ENDING_DAY = new Intl.DateTimeFormat("en-GB", {
  dateStyle: "medium",
  timeZone: "Europe/London",
});

/** Whether the day chosen is one an ending may be set for: today or later. */
const mayEndOn = (day: string): boolean =>
  EndInvolvementsRequestSchema.shape.endsOn.safeParse(day).success;

/** Why the day chosen may not be the ending's day, as its message key. */
const dayRefusal = ({
  day,
  involvement,
}: {
  day: string;
  involvement: Pick<PersonInvolvement, "endedAt">;
}): "endsOnPast" | "endsOnAlready" | undefined => {
  if (!mayEndOn(day)) {
    return "endsOnPast";
  }
  return isInvolvementEndingOn({ involvement, day })
    ? "endsOnAlready"
    : undefined;
};

/** Whether a reason is one an ending may carry: not too long. */
const mayGiveAsReason = (reason: string): boolean =>
  EndInvolvementsRequestSchema.shape.reason.safeParse(reason).success;

export type EndInvolvementDialogProps = {
  /** The involvement being ended, and only that one. */
  involvement: PersonInvolvement;
  /** The person's state, for warning that ending would leave them unassigned. */
  state?: PersonState | undefined;
  onClose: () => void;
  /**
   * Ends `involvement` alone, as it was when the dialog opened: the request
   * names no other.
   */
  onEnd: (request: EndInvolvementsRequest) => Promisable<InvolvementOutcome>;
  onRefresh: () => Promisable<void>;
};

/**
 * Ends one involvement: now or at the end of a chosen day, from a closed set
 * of causes, and, where the cause needs it, why in the words of whoever ends
 * it. No cause is chosen for them, so an ending never carries one nobody
 * picked.
 */
export const EndInvolvementDialog = ({
  involvement,
  state,
  onClose,
  onEnd,
  onRefresh,
}: EndInvolvementDialogProps) => {
  const { t } = useTranslation();
  const [cause, setCause] = useState<OfferedEndingCause | null>(null);
  const [reason, setReason] = useState("");
  const [timing, setTiming] = useState<"now" | "on_a_day">("now");
  const [endsOn, setEndsOn] = useState(today);
  const [status, setStatus] = useState<InvolvementChangeStatus | undefined>(
    undefined,
  );
  const offered = offeredEndingCausesByPartyKind[involvement.party.kind];
  const now = timing === "now";
  const refusal = now ? undefined : dayRefusal({ day: endsOn, involvement });
  const dated = refusal === undefined;
  const withinLimit = mayGiveAsReason(reason);
  const needsReason = cause !== null && isReasonNeededForEndingCause(cause);
  const explained = withinLimit && (!needsReason || reason.trim() !== "");
  const leavesUnassigned =
    state?.kind === "active" && WARNS_UNASSIGNED.includes(involvement.kind);

  const submit = async () => {
    const request = EndInvolvementsRequestSchema.safeParse({
      involvements: [
        {
          involvementId: involvement.id,
          endedAt: involvement.endedAt?.toISOString() ?? null,
        },
      ],
      cause,
      endsOn: now ? null : endsOn,
      reason,
    });

    // Unreachable while submit waits for a cause, `dated` and `explained`, which
    // apply the schema's own field checks and its rule for a cause that needs a
    // reason. Kept so a drift between them fails politely.
    /* v8 ignore if */
    if (!request.success) {
      setStatus("failed");
      return;
    }

    setStatus("submitting");
    const outcome = await outcomeOfChange(() => onEnd(request.data));

    if (outcome === "done") {
      onClose();
      return;
    }

    setStatus(outcome);
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("access.ending.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <DialogContentText>
            {t("access.ending.description")}
          </DialogContentText>

          <TextField
            select
            size="small"
            label={t("access.ending.cause")}
            value={cause ?? ""}
            slotProps={{
              inputLabel: { shrink: true },
              select: { displayEmpty: true },
            }}
            onChange={(event) => {
              const chosen = OfferedEndingCauseSchema.safeParse(
                event.target.value,
              );
              setCause(chosen.success ? chosen.data : null);
            }}
          >
            <MenuItem value="" disabled>
              {t("access.ending.causePlaceholder")}
            </MenuItem>
            {offered.map((offeredCause) => (
              <MenuItem key={offeredCause} value={offeredCause}>
                {t(
                  `access.ending.causes.${involvement.party.kind}.${offeredCause}`,
                )}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            required={needsReason}
            multiline
            minRows={3}
            size="small"
            label={t("access.ending.reason")}
            value={reason}
            error={!withinLimit}
            helperText={
              withinLimit
                ? t(
                    needsReason
                      ? "access.ending.reasonHintRequired"
                      : "access.ending.reasonHint",
                  )
                : t("access.ending.reasonInvalid")
            }
            onChange={(event) => {
              setReason(event.target.value);
            }}
          />

          <FormControl>
            <FormLabel id="ending-timing">
              {t("access.ending.timing")}
            </FormLabel>
            <RadioGroup
              aria-labelledby="ending-timing"
              value={timing}
              onChange={(event) => {
                setTiming(event.target.value === "now" ? "now" : "on_a_day");
              }}
            >
              <FormControlLabel
                value="now"
                control={<Radio size="small" />}
                label={t("access.ending.endNow")}
              />
              <FormControlLabel
                value="on_a_day"
                control={<Radio size="small" />}
                label={t("access.ending.endOnDay")}
              />
            </RadioGroup>
          </FormControl>

          {now ? null : (
            <TextField
              type="date"
              size="small"
              label={t("access.ending.endsOn")}
              value={endsOn}
              error={!dated}
              helperText={
                refusal === undefined
                  ? undefined
                  : t(`access.ending.${refusal}`)
              }
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { min: today() },
              }}
              onChange={(event) => {
                setEndsOn(event.target.value);
              }}
            />
          )}

          {dated ? (
            <Typography variant="body2">
              {now
                ? t("access.ending.consequenceNow", {
                    name: involvement.party.name,
                    role: t(`access.kind.${involvement.kind}`),
                  })
                : t("access.ending.consequence", {
                    name: involvement.party.name,
                    role: t(`access.kind.${involvement.kind}`),
                    date: ENDING_DAY.format(endOfDay(endsOn)),
                  })}
            </Typography>
          ) : null}

          {leavesUnassigned ? (
            <Alert severity="warning">
              {t("access.ending.becomesUnassigned", {
                role: t(`access.kind.${involvement.kind}`),
              })}
            </Alert>
          ) : null}

          {match(status)
            .with(undefined, "submitting", () => null)
            .with("failed", () => (
              <Alert severity="error">{t("access.ending.failed")}</Alert>
            ))
            .with("changed", () => (
              <ChangedSinceOpenedAlert
                message={t("access.ending.changed")}
                onRefresh={onRefresh}
                onClose={onClose}
              />
            ))
            .exhaustive()}
        </Stack>
      </DialogContent>
      <CancelOrProceedActions
        cancel={{
          onClick: onClose,
        }}
        proceed={{
          label: t("access.ending.submit"),
          onClick: submit,
          disabled:
            cause === null ||
            !dated ||
            !explained ||
            status === "submitting" ||
            status === "changed",
          kind: "destructive",
        }}
      />
    </Dialog>
  );
};
